import api from './axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface DashboardStats {
  totalFarms: number;
  totalPonds: number;
  totalFeedInventory: number;
  totalAnimalGroups: number;
  monthlyProduction: number;
  feedEfficiency: number;
  survivalRate: number;
  waterQualityScore: number;
}

export interface ChartData {
  biomassData: Array<{
    month: string;
    tilapia: number;
    catfish: number;
    shrimp: number;
  }>;
  feedConsumptionData: Array<{
    week: string;
    starter: number;
    grower: number;
    finisher: number;
  }>;
  waterQualityData: Array<{
    date: string;
    temperature: number;
    ph: number;
    dissolvedOxygen: number;
  }>;
  growthData: Array<{
    week: string;
    tilapia: number;
    catfish: number;
    shrimp: number;
  }>;
  hasRealWaterQualityData?: boolean;
}

export interface Alert {
  id: number;
  type: 'warning' | 'info' | 'success' | 'error';
  message: string;
  severity: 'warning' | 'info' | 'success' | 'error';
  timestamp: string;
  dismissed?: boolean;
}

// Type guard to check if an object is DashboardStats
function isDashboardStats(obj: any): obj is DashboardStats {
  return obj && typeof obj === 'object' && 'totalFarms' in obj && 'totalPonds' in obj;
}

// Type guard to check if an object is ChartData
function isChartData(obj: any): obj is ChartData {
  return obj && typeof obj === 'object' && 'biomassData' in obj && 'feedConsumptionData' in obj;
}

// Type guard to check if an object is an Alert array
function isAlertArray(obj: any): obj is Alert[] {
  return Array.isArray(obj) && obj.every(item => item && typeof item === 'object' && 'id' in item && 'message' in item);
}

// Helper function to get translated chart data
export const getTranslatedChartData = (t: (key: string) => string): ChartData => {
  return {
    biomassData: [
      { month: t('Day 1'), tilapia: 120, catfish: 80, shrimp: 40 },
      { month: t('Day 2'), tilapia: 180, catfish: 120, shrimp: 60 },
      { month: t('Day 3'), tilapia: 240, catfish: 160, shrimp: 80 },
      { month: t('Day 4'), tilapia: 300, catfish: 200, shrimp: 100 },
      { month: t('Day 5'), tilapia: 360, catfish: 240, shrimp: 120 },
      { month: t('Day 6'), tilapia: 420, catfish: 280, shrimp: 140 },
      { month: t('Day 7'), tilapia: 480, catfish: 320, shrimp: 160 },
      { month: t('Day 8'), tilapia: 540, catfish: 360, shrimp: 180 },
      { month: t('Day 9'), tilapia: 600, catfish: 400, shrimp: 200 },
      { month: t('Day 10'), tilapia: 660, catfish: 440, shrimp: 220 },
      { month: t('Day 11'), tilapia: 720, catfish: 480, shrimp: 240 },
      { month: t('Day 12'), tilapia: 780, catfish: 520, shrimp: 260 },
      { month: t('Day 13'), tilapia: 840, catfish: 560, shrimp: 280 },
      { month: t('Day 14'), tilapia: 900, catfish: 600, shrimp: 300 },
    ],
    feedConsumptionData: [
      { week: t('Day 1'), starter: 15, grower: 20, finisher: 10 },
      { week: t('Day 2'), starter: 18, grower: 25, finisher: 12 },
      { week: t('Day 3'), starter: 20, grower: 30, finisher: 15 },
      { week: t('Day 4'), starter: 22, grower: 35, finisher: 18 },
      { week: t('Day 5'), starter: 24, grower: 40, finisher: 20 },
      { week: t('Day 6'), starter: 26, grower: 45, finisher: 22 },
      { week: t('Day 7'), starter: 28, grower: 50, finisher: 25 },
      { week: t('Day 8'), starter: 30, grower: 55, finisher: 28 },
      { week: t('Day 9'), starter: 32, grower: 60, finisher: 30 },
      { week: t('Day 10'), starter: 34, grower: 65, finisher: 32 },
      { week: t('Day 11'), starter: 36, grower: 70, finisher: 35 },
      { week: t('Day 12'), starter: 38, grower: 75, finisher: 38 },
      { week: t('Day 13'), starter: 40, grower: 80, finisher: 40 },
      { week: t('Day 14'), starter: 42, grower: 85, finisher: 42 },
    ],
    waterQualityData: [
      { date: '01/01', temperature: 28.5, ph: 7.2, dissolvedOxygen: 6.8 },
      { date: '01/02', temperature: 29.0, ph: 7.1, dissolvedOxygen: 6.5 },
      { date: '01/03', temperature: 27.8, ph: 7.3, dissolvedOxygen: 7.2 },
      { date: '01/04', temperature: 28.2, ph: 7.0, dissolvedOxygen: 6.9 },
      { date: '01/05', temperature: 29.5, ph: 7.4, dissolvedOxygen: 7.0 },
      { date: '01/06', temperature: 28.8, ph: 7.2, dissolvedOxygen: 6.7 },
      { date: '01/07', temperature: 28.1, ph: 7.1, dissolvedOxygen: 6.9 },
      { date: '01/08', temperature: 29.2, ph: 7.3, dissolvedOxygen: 7.1 },
      { date: '01/09', temperature: 27.9, ph: 7.0, dissolvedOxygen: 6.8 },
      { date: '01/10', temperature: 28.6, ph: 7.2, dissolvedOxygen: 7.0 },
      { date: '01/11', temperature: 29.1, ph: 7.4, dissolvedOxygen: 6.6 },
      { date: '01/12', temperature: 28.3, ph: 7.1, dissolvedOxygen: 7.3 },
      { date: '01/13', temperature: 28.9, ph: 7.3, dissolvedOxygen: 6.9 },
      { date: '01/14', temperature: 27.7, ph: 7.0, dissolvedOxygen: 7.1 },
    ],
    growthData: [
      { week: t('Day 1'), tilapia: 5, catfish: 10, shrimp: 1 },
      { week: t('Day 2'), tilapia: 15, catfish: 25, shrimp: 3 },
      { week: t('Day 3'), tilapia: 35, catfish: 50, shrimp: 8 },
      { week: t('Day 4'), tilapia: 60, catfish: 85, shrimp: 15 },
      { week: t('Day 5'), tilapia: 90, catfish: 120, shrimp: 22 },
      { week: t('Day 6'), tilapia: 125, catfish: 160, shrimp: 30 },
      { week: t('Day 7'), tilapia: 160, catfish: 200, shrimp: 38 },
      { week: t('Day 8'), tilapia: 195, catfish: 240, shrimp: 45 },
      { week: t('Day 9'), tilapia: 230, catfish: 280, shrimp: 52 },
      { week: t('Day 10'), tilapia: 265, catfish: 320, shrimp: 59 },
      { week: t('Day 11'), tilapia: 300, catfish: 360, shrimp: 66 },
      { week: t('Day 12'), tilapia: 335, catfish: 400, shrimp: 73 },
      { week: t('Day 13'), tilapia: 370, catfish: 440, shrimp: 80 },
      { week: t('Day 14'), tilapia: 405, catfish: 480, shrimp: 87 },
    ],
    hasRealWaterQualityData: false,
  };
};

// Helper function to get translated alerts
export const getTranslatedAlerts = (t: (key: string) => string): Alert[] => {
  return [
    { 
      id: 1, 
      type: 'warning', 
      message: t('Pond 3 water temperature is above optimal range'), 
      severity: 'warning',
      timestamp: new Date().toISOString()
    },
    { 
      id: 2, 
      type: 'info', 
      message: t('Feed inventory for starter feed is running low'), 
      severity: 'info',
      timestamp: new Date().toISOString()
    },
    { 
      id: 3, 
      type: 'success', 
      message: t('Harvest scheduled for Pond 1 next week'), 
      severity: 'success',
      timestamp: new Date().toISOString()
    },
  ];
};

export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const response = await api.get<BackendResponse<DashboardStats>>('/analytics/dashboard-stats');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a DashboardStats object
    if (isDashboardStats(responseData)) {
      return responseData;
    }
    
    console.error('Invalid dashboard stats response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    // Return mock data if API is not available
    return {
      totalFarms: 5,
      totalPonds: 12,
      totalFeedInventory: 2500,
      totalAnimalGroups: 8,
      monthlyProduction: 8400,
      feedEfficiency: 1.8,
      survivalRate: 92,
      waterQualityScore: 87
    };
  }
};

export const fetchChartData = async (): Promise<ChartData> => {
  console.log('🔍 fetchChartData: Starting request to /analytics/chart-data');
  try {
    const response = await api.get<BackendResponse<ChartData>>('/analytics/chart-data');
    console.log('✅ fetchChartData: Received response:', response.data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      console.log('✅ fetchChartData: Using wrapped response data');
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a ChartData object
    if (isChartData(responseData)) {
      console.log('✅ fetchChartData: Using direct response data');
      return responseData;
    }
    
    console.error('❌ fetchChartData: Invalid chart data response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('❌ fetchChartData: Error fetching chart data:', error);
    // Return mock data if API is not available - this will be translated in the component
    return {
      biomassData: [
        { month: 'Day 1', tilapia: 120, catfish: 80, shrimp: 40 },
        { month: 'Day 2', tilapia: 180, catfish: 120, shrimp: 60 },
        { month: 'Day 3', tilapia: 240, catfish: 160, shrimp: 80 },
        { month: 'Day 4', tilapia: 300, catfish: 200, shrimp: 100 },
        { month: 'Day 5', tilapia: 360, catfish: 240, shrimp: 120 },
        { month: 'Day 6', tilapia: 420, catfish: 280, shrimp: 140 },
        { month: 'Day 7', tilapia: 480, catfish: 320, shrimp: 160 },
        { month: 'Day 8', tilapia: 540, catfish: 360, shrimp: 180 },
        { month: 'Day 9', tilapia: 600, catfish: 400, shrimp: 200 },
        { month: 'Day 10', tilapia: 660, catfish: 440, shrimp: 220 },
        { month: 'Day 11', tilapia: 720, catfish: 480, shrimp: 240 },
        { month: 'Day 12', tilapia: 780, catfish: 520, shrimp: 260 },
        { month: 'Day 13', tilapia: 840, catfish: 560, shrimp: 280 },
        { month: 'Day 14', tilapia: 900, catfish: 600, shrimp: 300 },
      ],
      feedConsumptionData: [
        { week: 'Day 1', starter: 15, grower: 20, finisher: 10 },
        { week: 'Day 2', starter: 18, grower: 25, finisher: 12 },
        { week: 'Day 3', starter: 20, grower: 30, finisher: 15 },
        { week: 'Day 4', starter: 22, grower: 35, finisher: 18 },
        { week: 'Day 5', starter: 24, grower: 40, finisher: 20 },
        { week: 'Day 6', starter: 26, grower: 45, finisher: 22 },
        { week: 'Day 7', starter: 28, grower: 50, finisher: 25 },
        { week: 'Day 8', starter: 30, grower: 55, finisher: 28 },
        { week: 'Day 9', starter: 32, grower: 60, finisher: 30 },
        { week: 'Day 10', starter: 34, grower: 65, finisher: 32 },
        { week: 'Day 11', starter: 36, grower: 70, finisher: 35 },
        { week: 'Day 12', starter: 38, grower: 75, finisher: 38 },
        { week: 'Day 13', starter: 40, grower: 80, finisher: 40 },
        { week: 'Day 14', starter: 42, grower: 85, finisher: 42 },
      ],
      waterQualityData: [
        { date: '01/01', temperature: 28.5, ph: 7.2, dissolvedOxygen: 6.8 },
        { date: '01/02', temperature: 29.0, ph: 7.1, dissolvedOxygen: 6.5 },
        { date: '01/03', temperature: 27.8, ph: 7.3, dissolvedOxygen: 7.2 },
        { date: '01/04', temperature: 28.2, ph: 7.0, dissolvedOxygen: 6.9 },
        { date: '01/05', temperature: 29.5, ph: 7.4, dissolvedOxygen: 7.0 },
        { date: '01/06', temperature: 28.8, ph: 7.2, dissolvedOxygen: 6.7 },
        { date: '01/07', temperature: 28.1, ph: 7.1, dissolvedOxygen: 6.9 },
        { date: '01/08', temperature: 29.2, ph: 7.3, dissolvedOxygen: 7.1 },
        { date: '01/09', temperature: 27.9, ph: 7.0, dissolvedOxygen: 6.8 },
        { date: '01/10', temperature: 28.6, ph: 7.2, dissolvedOxygen: 7.0 },
        { date: '01/11', temperature: 29.1, ph: 7.4, dissolvedOxygen: 6.6 },
        { date: '01/12', temperature: 28.3, ph: 7.1, dissolvedOxygen: 7.3 },
        { date: '01/13', temperature: 28.9, ph: 7.3, dissolvedOxygen: 6.9 },
        { date: '01/14', temperature: 27.7, ph: 7.0, dissolvedOxygen: 7.1 },
      ],
      growthData: [
        { week: 'Day 1', tilapia: 5, catfish: 10, shrimp: 1 },
        { week: 'Day 2', tilapia: 15, catfish: 25, shrimp: 3 },
        { week: 'Day 3', tilapia: 35, catfish: 50, shrimp: 8 },
        { week: 'Day 4', tilapia: 60, catfish: 85, shrimp: 15 },
        { week: 'Day 5', tilapia: 90, catfish: 120, shrimp: 22 },
        { week: 'Day 6', tilapia: 125, catfish: 160, shrimp: 30 },
        { week: 'Day 7', tilapia: 160, catfish: 200, shrimp: 38 },
        { week: 'Day 8', tilapia: 195, catfish: 240, shrimp: 45 },
        { week: 'Day 9', tilapia: 230, catfish: 280, shrimp: 52 },
        { week: 'Day 10', tilapia: 265, catfish: 320, shrimp: 59 },
        { week: 'Day 11', tilapia: 300, catfish: 360, shrimp: 66 },
        { week: 'Day 12', tilapia: 335, catfish: 400, shrimp: 73 },
        { week: 'Day 13', tilapia: 370, catfish: 440, shrimp: 80 },
        { week: 'Day 14', tilapia: 405, catfish: 480, shrimp: 87 },
      ],
      hasRealWaterQualityData: false,
    };
  }
};

export const fetchAlerts = async (): Promise<Alert[]> => {
  try {
    const response = await api.get<BackendResponse<Alert[]>>('/analytics/alerts');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (isAlertArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid alerts response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching alerts:', error);
    // Return mock alerts if API is not available - this will be translated in the component
    return [
      { 
        id: 1, 
        type: 'warning', 
        message: 'Pond 3 water temperature is above optimal range', 
        severity: 'warning',
        timestamp: new Date().toISOString()
      },
      { 
        id: 2, 
        type: 'info', 
        message: 'Feed inventory for starter feed is running low', 
        severity: 'info',
        timestamp: new Date().toISOString()
      },
      { 
        id: 3, 
        type: 'success', 
        message: 'Harvest scheduled for Pond 1 next week', 
        severity: 'success',
        timestamp: new Date().toISOString()
      },
    ];
  }
};

export const dismissAlert = async (alertId: number): Promise<boolean> => {
  try {
    await api.put(`/analytics/alerts/${alertId}/dismiss`);
    return true;
  } catch (error) {
    console.error('Error dismissing alert:', error);
    return false;
  }
}; 